// Route: /tutor/[sessionId] — главный экран AI-чата (3-mode, streaming).
// Lazy split — heavy bundle (virtualization, markdown, citation preview).
//
// URL state: ?mode, ?demo, ?msgIndex
//
// См. docs/ui-spec/tutor.[sessionId].{desktop,mobile}.md.

import { tolerantSearch } from "@studyhub/shared/utils/tolerant-search";
import { Skeleton } from "@studyhub/ui";
import { createFileRoute, useParams, useSearch } from "@tanstack/react-router";
import { lazy, Suspense } from "react";
import { z } from "zod";
import { LazyRouteBoundary } from "@/components/shared/lazy-route-boundary";

const TutorChat = lazy(() => import("@/components/tutor/chat/tutor-chat"));

const searchSchema = z.object({
	mode: z.enum(["regular", "materials", "web-search"]).optional(),
	// Phase 3.5 sweep: убраны `streaming` / `web-search` демо (требовали mock-данных).
	demo: z.enum(["default", "loading", "error", "empty", "quota-exceeded"]).optional(),
	msgIndex: z.coerce.number().int().nonnegative().optional(),
	/** UUID сценария — приходит из ScenarioCard «Запустить» / deep-link. */
	scenario: z.string().uuid().optional(),
	/** Тема для авто-старта чата (приходит из weak-spot «Объяснить через AI»). */
	topic: z.string().min(1).max(500).optional(),
	/** Готовый вопрос пользователя для авто-отправки (sample-вопросы из AskCard). */
	q: z.string().min(1).max(2000).optional(),
	/** Material для materials-режима при авто-старте (seed materialsScope). */
	materialId: z.string().uuid().optional(),
	/** Коллекция в контексте чата (точка входа «Спросить AI по коллекции»). */
	setId: z.string().uuid().optional(),
});

export const Route = createFileRoute("/tutor/$sessionId")({
	validateSearch: tolerantSearch(searchSchema),
	component: TutorSessionRoute,
});

function TutorSessionRoute(): React.ReactElement {
	const { sessionId } = useParams({ from: "/tutor/$sessionId" });
	const search = useSearch({ from: "/tutor/$sessionId" });
	return (
		<LazyRouteBoundary>
			<Suspense fallback={<ChatRouteSkeleton />}>
				<TutorChat
					sessionId={sessionId}
					demo={search.demo}
					initialMode={search.mode ?? (search.materialId || search.setId ? "materials" : undefined)}
					initialScenarioId={search.scenario}
					initialTopic={search.topic}
					initialPrompt={search.q}
					initialMaterialId={search.materialId}
					initialCollectionId={search.setId}
				/>
			</Suspense>
		</LazyRouteBoundary>
	);
}

function ChatRouteSkeleton(): React.ReactElement {
	return (
		<div className="flex h-fit-app flex-col">
			{/* Header + mode segment (одна строка) */}
			<div className="flex h-12 items-center gap-2 border-b border-border px-3 sm:gap-3 lg:px-6">
				<Skeleton className="size-7 shrink-0 rounded-[var(--radius-tile)]" />
				<Skeleton className="h-4 w-28 shrink-0 sm:w-40" />
				<div className="flex flex-1 justify-center">
					<Skeleton className="h-9 w-full max-w-[420px] rounded-[var(--radius-md)]" />
				</div>
				<Skeleton className="size-9 shrink-0 rounded-[var(--radius-md)]" />
			</div>
			{/* Messages */}
			<div className="mx-auto w-full max-w-[720px] flex-1 space-y-5 overflow-hidden px-4 py-4 lg:px-6">
				{/* Assistant = text lines; user = white card bubble. */}
				<div className="space-y-2">
					<Skeleton className="h-3 w-full rounded-md" />
					<Skeleton className="h-3 w-4/5 rounded-md" />
					<Skeleton className="h-3 w-3/5 rounded-md" />
				</div>
				<Skeleton className="ml-auto h-14 w-3/5 rounded-[var(--radius-card)] border border-border bg-card" />
				<div className="space-y-2">
					<Skeleton className="h-3 w-full rounded-md" />
					<Skeleton className="h-3 w-5/6 rounded-md" />
					<Skeleton className="h-3 w-2/3 rounded-md" />
				</div>
				<Skeleton className="ml-auto h-12 w-2/5 rounded-[var(--radius-card)] border border-border bg-card" />
			</div>
			{/* Composer */}
			<div className="border-t border-border px-4 py-3 lg:px-6">
				<Skeleton className="mx-auto h-12 w-full max-w-[720px] rounded-[var(--radius-lg)]" />
			</div>
		</div>
	);
}
